import { useEffect } from 'react';
import { getTwilioClient } from './twilio-chat';

export const useTwilioChannelUnreadSubscriber = ({ getUnreadCount, userEmail }) => {
    useEffect(() => {
        if (!userEmail || !getUnreadCount) return;

        let isMounted = true;
        const attachedListeners = new Map();
        let client;

        const attachMessageListener = channel => {
            const sid = channel.sid;
            if (attachedListeners.has(sid)) return;

            const onMessageAdded = msg => {
                if (!isMounted) return;
                if (msg.state?.author === userEmail) return;
                getUnreadCount();
            };

            channel.on('messageAdded', onMessageAdded);
            attachedListeners.set(sid, onMessageAdded);
        };

        const attachListenersToExistingChannels = async () => {
            client = await getTwilioClient();
            let paginator = await client.getSubscribedChannels();

            while (paginator) {
                for (const channel of paginator.items) {
                    attachMessageListener(channel);
                }
                paginator = paginator.hasNextPage ? await paginator.nextPage() : null;
            }

            client.on('channelJoined', channel => {
                attachMessageListener(channel);
            });
        };

        attachListenersToExistingChannels();

        return () => {
            isMounted = false;

            const cleanup = async () => {
                if (!client) return;
                let paginator = await client.getSubscribedChannels();

                while (paginator) {
                    for (const channel of paginator.items) {
                        const sid = channel.sid;
                        const listener = attachedListeners.get(sid);
                        if (listener) {
                            channel.removeListener('messageAdded', listener);
                            attachedListeners.delete(sid);
                        }
                    }
                    paginator = paginator.hasNextPage ? await paginator.nextPage() : null;
                }

                client.removeAllListeners('channelJoined');
            };

            cleanup();
        };
    }, [getUnreadCount, userEmail]);
};
